import { ConversationRef, userScopedKey } from "./later";

/**
 * Activity feed — per-browser (localStorage), like "Later", namespaced
 * per logged-in uid. Honest limitation, same as later.ts: this only logs
 * things that happened while dbm. chat was open in this browser, not a
 * synced server-side event log.
 */

export type ActivityKind = "message" | "mention" | "channel-created" | "added-to-channel";

export type ActivityItem = {
  id: string;
  kind: ActivityKind;
  text: string;
  from?: string;
  conversation?: ConversationRef;
  at: number;
  /** True if this conversation was already open (so you saw the message
   * live) when it was logged — excluded from the unseen badge count even
   * though it's still kept in the history list. */
  seenLive?: boolean;
};

const KEY = "dbm-activity-items";
const SEEN_KEY = "dbm-activity-seen-at";
const MAX_ITEMS = 100;

type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeActivity(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function loadActivity(): ActivityItem[] {
  try {
    const raw = localStorage.getItem(userScopedKey(KEY));
    return raw ? (JSON.parse(raw) as ActivityItem[]) : [];
  } catch {
    return [];
  }
}

export function addActivityItem(item: Omit<ActivityItem, "id" | "at">): void {
  const items = [{ ...item, id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, at: Date.now() }, ...loadActivity()].slice(
    0,
    MAX_ITEMS
  );
  try {
    localStorage.setItem(userScopedKey(KEY), JSON.stringify(items));
  } catch {
    /* storage full/blocked — degrade silently */
  }
  listeners.forEach((fn) => fn());
}

/** Strips any logged activity pointing at a conversation you no longer
 * have access to (left/kicked from a channel, channel deleted) — so
 * there's no leftover "New message · #general" entry that still opens a
 * conversation you can't actually see anymore. */
export function removeActivityForConversation(kind: "user" | "group", id: string): void {
  const next = loadActivity().filter(
    (i) => !(i.conversation?.kind === kind && i.conversation?.id === id)
  );
  try {
    localStorage.setItem(userScopedKey(KEY), JSON.stringify(next));
  } catch {
    /* ignore */
  }
  listeners.forEach((fn) => fn());
}

export function clearActivity(): void {
  try {
    localStorage.setItem(userScopedKey(KEY), JSON.stringify([]));
  } catch {
    /* ignore */
  }
  listeners.forEach((fn) => fn());
}

/** Timestamp the Activity list was last opened. 0 = never, so a first-time
 * user sees a badge for whatever's already been logged. */
function seenAt(): number {
  try {
    return Number(localStorage.getItem(userScopedKey(SEEN_KEY))) || 0;
  } catch {
    return 0;
  }
}

/** How many activity items have arrived since the panel was last opened —
 * drives the rail badge, so there's a visible cue without opening it. */
export function unseenActivityCount(): number {
  const since = seenAt();
  return loadActivity().filter((i) => i.at > since && !i.seenLive).length;
}

/** Called when the Activity panel opens — clears the badge. */
export function markActivitySeen(): void {
  try {
    localStorage.setItem(userScopedKey(SEEN_KEY), String(Date.now()));
  } catch {
    /* ignore */
  }
  listeners.forEach((fn) => fn());
}
